import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';

import {
	NavigationMenu,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import type { SiteHeaderProps } from '@/types/theme';

function normalizeUrl(url?: string) {
	return (url || '').replace(/\/$/, '');
}

function isCtaMenuItem(classes?: string[]) {
	if (!classes?.length) return false;
	return classes.some((c) => ['cta', 'is-cta', 'menu-item-cta'].includes(c));
}

export function SiteNavbar({
	siteName,
	siteUrl,
	tagline,
	siteIconUrl,
	customLogoUrl,
	customLogoAlt,
	menuItems = [],
	headerCtaLabel,
	headerCtaUrl,
	navbarClasses,
	navAreaClasses,
	ctaAreaClasses,
}: SiteHeaderProps) {
	const [mobileOpen, setMobileOpen] = useState(false);
	const initial = (siteName ?? 'G').charAt(0).toUpperCase();
	const brandUrl = siteUrl || '/';
	const ctaUrl = headerCtaUrl || '/';

	const hasExplicitCta = Boolean(headerCtaLabel && headerCtaUrl);
	const explicitCta = hasExplicitCta
		? {
				id: -1,
				title: headerCtaLabel || '',
				url: ctaUrl,
				target: undefined as string | undefined,
				customClassName: undefined as string | undefined,
				isCta: true,
				current: false,
			}
		: null;

	const regularMenuItems = menuItems.filter((item) => {
		if (item.isCta || isCtaMenuItem(item.classes)) return false;
		if (!explicitCta) return true;
		return !(
			normalizeUrl(item.url) === normalizeUrl(explicitCta.url) &&
			item.title.trim() === explicitCta.title.trim()
		);
	});

	const ctaItems = explicitCta
		? [explicitCta]
		: menuItems.filter((item) => item.isCta || isCtaMenuItem(item.classes));

	return (
		<header className="pointer-events-none relative inset-x-0 top-0 z-50 flex justify-center px-4   md:px-8  pt-4">
			<motion.div
				className={cn('pointer-events-auto w-full max-w-5xl', navbarClasses)}
				initial={{ opacity: 0, y: -16, scale: 0.98 }}
				animate={{ opacity: 1, y: 0, scale: 1 }}
				transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
			>
				{/* ── pill shell ─────────────────────────────── */}
				<div className="relative overflow-hidden rounded-[1.125rem] border border-foreground/[0.07] bg-card/90 shadow-[0_2px_4px_rgba(27,27,27,0.04),0_8px_24px_rgba(27,27,27,0.08)] backdrop-blur-xl">
					{/* top-edge highlight */}
					<div
						aria-hidden="true"
						className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent"
					/>

					{/* ── main bar ─────────────────────────── */}
					<div className="flex h-14 items-center gap-0 px-2 md:px-3">
						{/* logo */}
						<a
							href={brandUrl}
							className="group flex shrink-0 items-center gap-2.5 rounded-xl px-2 py-1.5 transition-colors hover:bg-foreground/[0.04]"
							aria-label={siteName || 'Home'}
						>
							{customLogoUrl ? (
								<img
									className="h-8 w-8 rounded-full object-cover ring-1 ring-black/10 transition-transform duration-300 group-hover:scale-105"
									src={customLogoUrl}
									alt={customLogoAlt || siteName || ''}
								/>
							) : siteIconUrl ? (
								<img
									className="h-8 w-8 rounded-full object-cover ring-1 ring-black/10 transition-transform duration-300 group-hover:scale-105"
									src={siteIconUrl}
									alt={siteName || ''}
								/>
							) : (
								<span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-accent text-[11px] font-bold uppercase tracking-[0.18em] text-accent-foreground ring-1 ring-accent/30 transition-transform duration-300 group-hover:scale-105">
									{initial}
								</span>
							)}
						</a>

						{/* logo / nav divider */}
						<div
							aria-hidden="true"
							className="mx-1.5 hidden h-5 w-px shrink-0 bg-foreground/[0.09] md:block"
						/>

						{/* nav area */}
						<div className={cn('hidden min-w-0 flex-1 items-center px-1 md:flex', navAreaClasses)}>
							<NavigationMenu className="min-w-0">
								<NavigationMenuList className="gap-0">
									{regularMenuItems.map((item) => (
										<NavigationMenuItem key={item.id}>
											<NavigationMenuLink asChild>
												<a
													href={item.url}
													target={item.target || undefined}
													rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
													className={cn(
														'group relative inline-flex h-9 items-center rounded-[0.65rem] px-3.5 text-[13px] font-medium tracking-[0.005em] transition-colors duration-150',
														item.current
															? 'text-foreground'
															: 'text-foreground/50 hover:bg-foreground/[0.05] hover:text-foreground',
														item.customClassName
													)}
												>
													{item.title}
													{item.current && (
														<span
															aria-hidden="true"
															className="absolute bottom-1 left-1/2 h-[3px] w-[18px] -translate-x-1/2 rounded-full bg-accent"
														/>
													)}
												</a>
											</NavigationMenuLink>
										</NavigationMenuItem>
									))}
								</NavigationMenuList>
							</NavigationMenu>
						</div>

						{/* flex spacer when no nav */}
						{regularMenuItems.length === 0 && (
							<div className="hidden flex-1 md:block" />
						)}

						{/* divider before CTA */}
						{ctaItems.length > 0 && regularMenuItems.length > 0 && (
							<div
								aria-hidden="true"
								className="mx-3 hidden h-4 w-px shrink-0 bg-foreground/[0.09] md:block"
							/>
						)}

						{/* CTA buttons (desktop) */}
						<div className={cn('hidden shrink-0 items-center gap-2 md:flex', ctaAreaClasses)}>
							{ctaItems.map((item) => (
								<a
									key={item.id}
									href={item.url}
									target={item.target || undefined}
									rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
									className={cn(
										'inline-flex h-8 items-center rounded-[0.6rem] bg-accent px-4 text-[13px] font-semibold tracking-[0.01em] text-accent-foreground',
										'shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_1px_4px_rgba(184,154,50,0.3)]',
										'transition-all duration-200 hover:brightness-105 hover:shadow-[0_1px_0_rgba(255,255,255,0.15)_inset,0_2px_10px_rgba(184,154,50,0.4)]',
										item.customClassName
									)}
								>
									{item.title}
								</a>
							))}
						</div>

						{/* mobile toggle */}
						<div className="ml-auto md:hidden">
							<button
								type="button"
								className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/60 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
								aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
								aria-expanded={mobileOpen}
								onClick={() => setMobileOpen((v) => !v)}
							>
								<AnimatePresence mode="wait" initial={false}>
									{mobileOpen ? (
										<motion.span
											key="close"
											initial={{ rotate: -90, opacity: 0 }}
											animate={{ rotate: 0, opacity: 1 }}
											exit={{ rotate: 90, opacity: 0 }}
											transition={{ duration: 0.12 }}
										>
											<X className="h-[18px] w-[18px]" />
										</motion.span>
									) : (
										<motion.span
											key="open"
											initial={{ rotate: 90, opacity: 0 }}
											animate={{ rotate: 0, opacity: 1 }}
											exit={{ rotate: -90, opacity: 0 }}
											transition={{ duration: 0.12 }}
										>
											<Menu className="h-[18px] w-[18px]" />
										</motion.span>
									)}
								</AnimatePresence>
							</button>
						</div>
					</div>

					{/* ── mobile panel ─────────────────────── */}
					<AnimatePresence initial={false}>
						{mobileOpen && (
							<motion.div
								key="mobile-nav"
								initial={{ height: 0, opacity: 0 }}
								animate={{ height: 'auto', opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
								transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
								className="overflow-hidden"
							>
								<div className="border-t border-foreground/[0.06] px-2 pb-3 pt-2">
									<nav className="flex flex-col gap-0.5" aria-label="Mobile navigation">
										{regularMenuItems.map((item, index) => (
											<motion.a
												key={item.id}
												href={item.url}
												target={item.target || undefined}
												rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
												initial={{ opacity: 0, x: -6 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{ delay: index * 0.03, duration: 0.16 }}
												className={cn(
													'flex items-center rounded-xl px-3.5 py-2.5 text-[13px] font-medium transition-colors',
													item.current
														? 'bg-accent/[0.08] text-accent'
														: 'text-foreground/60 hover:bg-foreground/[0.05] hover:text-foreground',
													item.customClassName
												)}
											>
												{item.title}
											</motion.a>
										))}

										{ctaItems.length > 0 && regularMenuItems.length > 0 && (
											<div className="my-1.5 h-px bg-foreground/[0.06]" />
										)}

										{ctaItems.map((item, index) => (
											<motion.a
												key={item.id}
												href={item.url}
												target={item.target || undefined}
												rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
												initial={{ opacity: 0, x: -6 }}
												animate={{ opacity: 1, x: 0 }}
												transition={{ delay: (regularMenuItems.length + index) * 0.03, duration: 0.16 }}
												className={cn(
													'flex items-center rounded-xl bg-accent px-3.5 py-2.5 text-[13px] font-semibold text-accent-foreground',
													'shadow-[0_1px_4px_rgba(184,154,50,0.3)] transition-opacity hover:opacity-90',
													item.customClassName
												)}
											>
												{item.title}
											</motion.a>
										))}
									</nav>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</div>
			</motion.div>
		</header>
	);
}
